const configuredBaseUrl = import.meta.env.VITE_DAWN_API_URL?.trim() ?? ''
const apiBaseUrl = configuredBaseUrl.replace(/\/$/, '')

export type ApiRuntimeStatus = 'operational' | 'degraded'

export interface ApiHealth {
  service: string
  status: ApiRuntimeStatus
  pilotMode: string
  externalActionsEnabled: boolean
  hermesEnabled?: boolean
  hermesConfigured?: boolean
  scheduledHermesEnabled?: boolean
  timestamp: string
}

export interface ApiHermesStatus {
  enabled: boolean
  configured: boolean
  reachable: boolean
  endpointScope?: 'local' | 'remote' | string
  model?: string
  platform?: string
  health?: Record<string, unknown>
  features?: Record<string, unknown>
  endpoints?: string[]
  errorCode?: string
  error?: string
}

export interface ApiWorkflow {
  id: string
  organisation_id: string
  code: string
  name: string
  module: string
  version: number
  mode: string
  risk_level: 'low' | 'medium' | 'high' | 'critical'
  approval_policy: string
  release_stage: number
  schedule_expression: string | null
  enabled: number
  external_actions_enabled: number
  case_study_category: string
  inputs: string[]
  outputs: string[]
  metrics: string[]
}

export interface ApiRun {
  id: string
  workflow_code: string
  workflow_version: number
  trigger_type: 'manual' | 'scheduled' | 'source-event' | 'system'
  requested_by: string
  status: 'queued' | 'running' | 'awaiting-approval' | 'completed' | 'failed' | 'cancelled' | 'blocked'
  approval_required: number
  started_at: string | null
  finished_at: string | null
  duration_ms: number | null
  usage_cost_gbp: number
  estimated_manual_minutes: number
  verified_time_saved_minutes: number | null
  output_json?: string | null
  error_code?: string | null
  error_message?: string | null
  agent_status?: string | null
  external_run_id?: string | null
  last_event?: string | null
  approval_count?: number | null
  agent_usage_json?: string | null
  created_at: string
}

export interface ApiApproval {
  id: string
  workflow_run_id: string
  approval_type: string
  title: string
  summary: string
  risk_level: 'low' | 'medium' | 'high' | 'critical'
  estimated_external_cost_gbp: number
  status: 'pending' | 'approved' | 'approved-with-changes' | 'returned' | 'rejected' | 'expired' | 'cancelled'
  requested_at: string
  decided_at: string | null
  decided_by: string | null
  reviewer_comment: string | null
  expires_at: string | null
}

export interface ApiRunResponse {
  run: ApiRun
  created: boolean
  externalActionPerformed: false
}

export interface ApiApprovalDecision {
  approval: ApiApproval
  workflowNotified?: boolean
  externalActionPerformed: false
}

export class DawnApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'DawnApiError'
    this.status = status
    this.code = code
  }
}

export async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    credentials: 'include',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  })

  const payload = await response.json().catch(() => ({})) as Record<string, unknown>
  if (!response.ok) {
    const code = typeof payload.code === 'string' ? payload.code : `HTTP_${response.status}`
    const message = typeof payload.error === 'string' ? payload.error : 'The DAWN control plane request failed.'
    throw new DawnApiError(response.status, code, message)
  }

  return payload as T
}

export const dawnApi = {
  health: () => requestJson<ApiHealth>('/api/health'),
  hermes: () => requestJson<ApiHermesStatus>('/api/v1/hermes'),
  workflows: () => requestJson<{ workflows: ApiWorkflow[] }>('/api/v1/workflows'),
  runs: () => requestJson<{ runs: ApiRun[] }>('/api/v1/runs'),
  approvals: () => requestJson<{ approvals: ApiApproval[] }>('/api/v1/approvals'),
  launchWorkflow: (
    workflowCode: string,
    input: Record<string, unknown>,
    estimatedManualMinutes: number,
  ) => requestJson<ApiRunResponse>(`/api/v1/workflows/${encodeURIComponent(workflowCode)}/run`, {
    method: 'POST',
    body: JSON.stringify({ input, estimatedManualMinutes }),
  }),
  decideApproval: (
    approvalId: string,
    decision: 'approved' | 'returned' | 'rejected',
    comment: string,
  ) => requestJson<ApiApprovalDecision>(`/api/v1/approvals/${encodeURIComponent(approvalId)}/decision`, {
    method: 'POST',
    body: JSON.stringify({ decision, comment }),
  }),
}
